import React, { useCallback, useEffect, useState } from 'react';
import axios from '../../interceptors/adminAxios';
import { useConfirm } from '../../hooks/useConfirm';

interface Company {
  id: number;
  name: string;
  display_name: string;
  color: string;
  description: string;
  tariff: string | null;
}

interface User {
  id: number;
  login: string;
  email: string;
  user_group: string;
  created_at: string;
}

interface Device {
  id: number;
  device_id: string;
  display_name: string | null;
  last_seen_at: string | null;
  app_version: string | null;
  ip_address: string | null;
  online: boolean;
}

interface Props {
  companyId: number;
  company: Company | null;
  onBack: () => void;
}

// ── Styles ──────────────────────────────────────────────────────────────────
const glassCard: React.CSSProperties = {
  background: 'var(--glass-bg)',
  backdropFilter: 'blur(20px)',
  WebkitBackdropFilter: 'blur(20px)',
  border: '1px solid var(--glass-border)',
  borderRadius: 14,
};

const btnAccent: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 13px', borderRadius: 8, border: 'none',
  background: 'var(--accent)', color: '#fff',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const btnGhost: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '6px 13px', borderRadius: 8,
  border: '1px solid var(--glass-border)',
  background: 'transparent', color: 'var(--text-secondary)',
  fontSize: 12, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer',
};

const mfInput: React.CSSProperties = {
  width: '100%', background: 'var(--input-bg)', border: '1px solid var(--input-border)',
  borderRadius: 8, padding: '7px 12px', fontSize: 13, color: 'var(--text-primary)',
  fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box',
};

const TARIFFS = [
  { id: 'free',       label: 'Free',       desc: '1 device, basic features',               price: '£0/mo',   color: '#94a3b8' },
  { id: 'starter',    label: 'Starter',    desc: '5 devices, scheduling, analytics',        price: '£29/mo',  color: '#60a5fa' },
  { id: 'business',   label: 'Business',   desc: '25 devices, multi-user, all features',    price: '£99/mo',  color: '#a78bfa' },
  { id: 'enterprise', label: 'Enterprise', desc: 'Unlimited devices, white-label, API',     price: 'Custom',  color: '#f59e0b' },
];

const COLORS = ['#3b82f6','#10b981','#f59e0b','#ef4444','#7c3aed','#ec4899','#0891b2','#84cc16'];

// ── Field wrapper ─────────────────────────────────────────────────────────
const Mf: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: 'var(--text-disabled)', marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

// ── Section header ────────────────────────────────────────────────────────
const SectionHeader: React.FC<{ title: string; action?: React.ReactNode }> = ({ title, action }) => (
  <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
    <div style={{ fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.08em', color: 'var(--text-disabled)', flex: 1 }}>{title}</div>
    {action}
  </div>
);

// ── User modal ────────────────────────────────────────────────────────────
interface UserModalProps {
  companyId: number;
  initial: User | null;
  onClose: () => void;
  onSave: () => void;
}

const UserModal: React.FC<UserModalProps> = ({ companyId, initial, onClose, onSave }) => {
  const [login, setLogin]       = useState(initial?.login ?? '');
  const [email, setEmail]       = useState(initial?.email ?? '');
  const [password, setPassword] = useState('');
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  const handleSave = async () => {
    if (!email.trim()) { setError('Email is required'); return; }
    setSaving(true); setError('');
    try {
      if (initial) {
        await axios.patch(`/admin/companies/${companyId}/users/${initial.id}`, { login, email, ...(password ? { password } : {}) });
      } else {
        await axios.post(`/admin/companies/${companyId}/users`, { login, email, password });
      }
      onSave();
    } catch (e: any) {
      setError(e?.response?.data?.message ?? 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.65)', backdropFilter: 'blur(6px)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border-strong)', borderRadius: 14, padding: 22, width: 400, boxShadow: '0 24px 64px rgba(0,0,0,.6)' }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 16 }}>
          {initial ? 'Edit User' : 'New User'}
        </div>
        <Mf label="Name">
          <input value={login} onChange={e => setLogin(e.target.value)} placeholder="Full name" style={mfInput} />
        </Mf>
        <Mf label="Email">
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="email@example.com" style={mfInput} />
        </Mf>
        <Mf label={initial ? 'New Password (leave blank to keep)' : 'Password'}>
          <input type="password" value={password} onChange={e => setPassword(e.target.value)} style={mfInput} />
        </Mf>
        {error && <div style={{ fontSize: 12, color: '#f87171', marginBottom: 10 }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
          <button style={btnGhost} onClick={onClose}>Cancel</button>
          <button style={btnAccent} onClick={handleSave} disabled={saving}>{saving ? 'Saving…' : initial ? 'Save' : 'Create'}</button>
        </div>
      </div>
    </div>
  );
};

// ── Main component ────────────────────────────────────────────────────────
const AdminCompanyDetail: React.FC<Props> = ({ companyId, company: initCo, onBack }) => {
  const { confirm, confirmModal } = useConfirm();
  const [company, setCompany]   = useState<Company | null>(initCo);
  const [users, setUsers]       = useState<User[]>([]);
  const [devices, setDevices]   = useState<Device[]>([]);
  const [saving, setSaving]     = useState(false);
  const [userModal, setUserModal] = useState<{ open: boolean; user: User | null }>({ open: false, user: null });

  // Company editable fields
  const [displayName, setDisplayName] = useState(initCo?.display_name ?? '');
  const [desc, setDesc]               = useState(initCo?.description ?? '');
  const [color, setColor]             = useState(initCo?.color ?? '#3b82f6');
  const [tariff, setTariff]           = useState(initCo?.tariff ?? 'free');

  const loadUsers   = useCallback(async () => {
    try { const { data } = await axios.get<User[]>(`/admin/companies/${companyId}/users`);   setUsers(data);   } catch {}
  }, [companyId]);

  const loadDevices = useCallback(async () => {
    try { const { data } = await axios.get<Device[]>(`/admin/companies/${companyId}/devices`); setDevices(data); } catch {}
  }, [companyId]);

  useEffect(() => { loadUsers(); loadDevices(); }, [loadUsers, loadDevices]);

  const handleSaveInfo = async () => {
    if (!company) return;
    setSaving(true);
    try {
      await axios.patch(`/admin/companies/${company.id}`, { display_name: displayName, description: desc, color });
      setCompany(c => c ? { ...c, display_name: displayName, description: desc, color } : c);
    } catch {} finally { setSaving(false); }
  };

  const handleSaveTariff = async () => {
    if (!company) return;
    setSaving(true);
    try {
      await axios.patch(`/admin/companies/${company.id}`, { tariff });
      setCompany(c => c ? { ...c, tariff } : c);
    } catch {} finally { setSaving(false); }
  };

  const handleDeleteUser = async (u: User) => {
    if (!company || !await confirm({ title: `Delete user "${u.login}"?`, confirmLabel: 'Delete', cancelLabel: 'Cancel', variant: 'danger' })) return;
    try { await axios.delete(`/admin/companies/${company.name}/users/${u.id}`); loadUsers(); } catch {}
  };

  return (
    <>
      {/* Topbar */}
      <div style={{ height: 50, flexShrink: 0, background: 'var(--topbar-bg)', borderBottom: '1px solid var(--divider)', display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10 }}>
        <button style={{ ...btnGhost, padding: '4px 10px' }} onClick={onBack}>← Back</button>
        {company && (
          <>
            <div style={{ width: 22, height: 22, borderRadius: 6, background: company.color, flexShrink: 0 }} />
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>{company.display_name || company.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginLeft: 2 }}>{company.name}</div>
          </>
        )}
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>

        {/* ── Block 1: Company Info ── */}
        <div style={{ ...glassCard, padding: 16 }}>
          <SectionHeader title="Company Info" />
          <Mf label="Display Name">
            <input value={displayName} onChange={e => setDisplayName(e.target.value)} style={mfInput} />
          </Mf>
          <Mf label="Description">
            <input value={desc} onChange={e => setDesc(e.target.value)} style={mfInput} />
          </Mf>
          <Mf label="Color">
            <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
              {COLORS.map(c => (
                <div key={c} onClick={() => setColor(c)} style={{ width: 22, height: 22, borderRadius: '50%', background: c, cursor: 'pointer', border: color === c ? '2px solid #fff' : '2px solid transparent', boxShadow: color === c ? `0 0 0 2px ${c}` : 'none', transition: 'all .1s' }} />
              ))}
            </div>
          </Mf>
          {company && (
            <div style={{ marginTop: 4, padding: '8px 10px', background: 'var(--glass-hover)', borderRadius: 8, fontSize: 12, color: 'var(--text-secondary)' }}>
              <span style={{ color: 'var(--text-disabled)' }}>Slug:&nbsp;</span>{company.name}
              <span style={{ marginLeft: 14, color: 'var(--text-disabled)' }}>ID:&nbsp;</span>{company.id}
            </div>
          )}
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnAccent} onClick={handleSaveInfo} disabled={saving}>{saving ? 'Saving…' : 'Save Info'}</button>
          </div>
        </div>

        {/* ── Block 2: Tariff ── */}
        <div style={{ ...glassCard, padding: 16 }}>
          <SectionHeader title="Tariff Plan" />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {TARIFFS.map(t => {
              const sel = tariff === t.id;
              return (
                <div key={t.id} onClick={() => setTariff(t.id)} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, border: `1px solid ${sel ? t.color : 'var(--glass-border)'}`, background: sel ? `${t.color}14` : 'var(--glass-hover)', cursor: 'pointer', transition: 'all .12s' }}>
                  <div style={{ width: 10, height: 10, borderRadius: '50%', background: t.color, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: sel ? 700 : 500, color: sel ? t.color : 'var(--text-primary)' }}>{t.label}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-disabled)', marginTop: 1 }}>{t.desc}</div>
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: sel ? t.color : 'var(--text-secondary)', fontVariantNumeric: 'tabular-nums' }}>{t.price}</div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
            <button style={btnAccent} onClick={handleSaveTariff} disabled={saving}>{saving ? 'Saving…' : 'Apply Tariff'}</button>
          </div>
        </div>

        {/* ── Block 3: Users ── */}
        <div style={{ ...glassCard, padding: 16 }}>
          <SectionHeader title={`Users (${users.length})`} action={
            <button style={{ ...btnGhost, padding: '4px 10px', fontSize: 11 }} onClick={() => setUserModal({ open: true, user: null })}>+ Add</button>
          } />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {users.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-disabled)', padding: '10px 0' }}>No users yet</div>
            )}
            {users.map((u, i) => (
              <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < users.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                <div style={{ width: 28, height: 28, borderRadius: 8, background: 'var(--glass-hover)', border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: 'var(--accent-light)', flexShrink: 0 }}>
                  {u.login.charAt(0).toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.login}</div>
                  {u.email && <div style={{ fontSize: 11, color: 'var(--text-disabled)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.email}</div>}
                </div>
                <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                  <button style={{ ...btnGhost, padding: '3px 7px', fontSize: 10 }} onClick={() => setUserModal({ open: true, user: u })}>Edit</button>
                  <button style={{ ...btnGhost, padding: '3px 7px', fontSize: 10, color: '#f87171', borderColor: 'rgba(248,113,113,.2)' }} onClick={() => handleDeleteUser(u)}>✕</button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ── Block 4: Devices ── */}
        <div style={{ ...glassCard, padding: 16 }}>
          <SectionHeader title={`Devices (${devices.length})`} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {devices.length === 0 && (
              <div style={{ fontSize: 12, color: 'var(--text-disabled)', padding: '10px 0' }}>No devices registered</div>
            )}
            {devices.map((d, i) => (
              <div key={d.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < devices.length - 1 ? '1px solid var(--divider)' : 'none' }}>
                <div style={{ width: 8, height: 8, borderRadius: '50%', background: d.online ? '#4ade80' : '#94a3b8', boxShadow: d.online ? '0 0 6px #4ade80' : 'none', flexShrink: 0 }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.display_name || d.device_id}
                  </div>
                  <div style={{ fontSize: 10, fontFamily: 'JetBrains Mono, monospace', color: 'var(--text-disabled)', marginTop: 1 }}>
                    {d.device_id}{d.app_version ? ` · v${d.app_version}` : ''}{d.ip_address ? ` · ${d.ip_address}` : ''}
                  </div>
                </div>
                <div style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8, background: d.online ? 'rgba(74,222,128,.1)' : 'rgba(148,163,184,.1)', color: d.online ? '#4ade80' : '#94a3b8', flexShrink: 0 }}>
                  {d.online ? 'Online' : 'Offline'}
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      {userModal.open && company && (
        <UserModal
          companyId={companyId}
          initial={userModal.user}
          onClose={() => setUserModal({ open: false, user: null })}
          onSave={() => { setUserModal({ open: false, user: null }); loadUsers(); }}
        />
      )}
      {confirmModal}
    </>
  );
};

export default AdminCompanyDetail;
